import { Component, Input, ViewChild } from "@angular/core";
import { ChartData, ChartDataset, ChartEvent, ChartOptions, LegendElement, LegendItem } from "chart.js";
import DataLabelsPlugin from "chartjs-plugin-datalabels";
import { BaseChartDirective } from "ng2-charts";
import { chartOnClickBehavior, setCursorStyle } from "src/app/common/chartUtils";
import { convertNumberToAcronyms } from "src/app/common/numberUtils";
import { IChartData } from "src/app/models/api/dashboards.model";
import { BeautifyDisplayStringPipe } from "src/app/pipes/beautify-display-string/beautify-display-string.pipe";

@Component({
    selector: "app-horizontal-bar-chart",
    templateUrl: "./horizontal-bar-chart.component.html",
    styleUrls: ["./horizontal-bar-chart.component.scss"],
})
export class HorizontalBarChartComponent {
    @Input() public title: string;
    @Input() public tooltip: string;
    @Input() public yAxisLabel: "value" | "name" = "value";
    @Input() public xAxisLabel: string | null = null;
    @Input() public set seriesData(seriesData: IChartData[]) {
        this._populateChartOptions(Math.max(...seriesData.map((item) => item.value)));
        this._populateChartData(seriesData);
        setTimeout(() => this.chart?.update(), 250);
    }
    @ViewChild(BaseChartDirective) public chart?: BaseChartDirective;

    public chartData: ChartData<"bar">;
    public chartOptions: ChartOptions<"bar">;

    public dataLabelsPlugin = DataLabelsPlugin;

    constructor(private _beautifyDisplayStringPipe: BeautifyDisplayStringPipe) {}

    private _populateChartData(seriesData: IChartData[]): void {
        const dataSets: ChartDataset<"bar">[] = [];
        for (const [index, data] of seriesData.entries()) {
            const currentData = new Array<number>(seriesData.length).fill(0);
            currentData[index] = data.value;

            dataSets.push({
                data: currentData,
                label: this._beautifyDisplayStringPipe.transform(data.name),
                backgroundColor: data.itemStyle.color,
                hoverBackgroundColor: data.itemStyle.color,
                borderRadius: 4,
            });
        }

        this.chartData = {
            labels: seriesData.map((data) => (this.yAxisLabel == "value" ? convertNumberToAcronyms(data.value) : data.name)),
            datasets: dataSets,
        };

        this.xAxisLabel?.let((label) =>
            this.chartOptions?.scales?.["x"]?.let(
                (xAxis) =>
                    (xAxis.title = {
                        text: label,
                        align: "center",
                        display: true,
                    }),
            ),
        );
    }

    private _populateChartOptions(maxValue: number): void {
        this.chartOptions = {
            maintainAspectRatio: false, // <-- Maintain the original canvas aspect ratio (width / height) when resizing. Setting to false to will be kept in the div container ratio.
            responsive: true, // <-- Resizes the chart canvas when its container does

            font: {
                family: "Basis Grotesque Pro",
            },
            indexAxis: "y",
            scales: {
                x: {
                    grid: {
                        display: false, // <-- this option disables the background grid
                    },
                    ticks: {
                        color: "#000000",
                    },
                    beginAtZero: true,
                    suggestedMax: maxValue * 1.1,
                    title: {
                        display: false,
                        font: {
                            size: 14,
                        },
                        color: "#696879",
                    },
                },
                y: {
                    grid: {
                        drawOnChartArea: false,
                        display: false, // <-- this option disables the background gird
                    },
                    ticks: {
                        font: {
                            size: 14,
                        },
                        color: "#696879",
                    },
                    beginAtZero: true,
                    stacked: true,
                },
            },
            plugins: {
                legend: {
                    display: this.yAxisLabel == "value",
                    position: "bottom",
                    labels: {
                        boxWidth: 5,
                        boxHeight: 5,
                        usePointStyle: true,
                        pointStyle: "circle",
                        font: {
                            size: 14,
                            family: "Basis Grotesque Pro",
                            weight: "400",
                        },
                    },
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    onHover(e: ChartEvent, legendItem: LegendItem, legend: LegendElement<"bar">) {
                        setCursorStyle(e?.native?.target as HTMLElement, "pointer");
                    },
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    onLeave(e: ChartEvent, legendItem: LegendItem, legend: LegendElement<"bar">) {
                        setCursorStyle(e?.native?.target as HTMLElement, "auto");
                    },
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    onClick(e: ChartEvent, legendItem: LegendItem, legend: LegendElement<"bar">) {
                        if (legendItem.datasetIndex === undefined) {
                            return;
                        }

                        chartOnClickBehavior(this.chart, legendItem.datasetIndex);
                    },
                },
                tooltip: {
                    enabled: false, // <-- this option disables tooltips
                },
                datalabels: {
                    // <--- this option handles all the data inside the graph
                    display: false, // <--- this option remove the value of the bar from showing on the bar itself
                },
            },
        };
    }
}
